import { useState, useCallback } from "react";
import { getErrorMessage } from "../lib/errors/getErrorMessage";

/**
 * `forFullSeries` is what the two-button series variant reports back — the owner routes take it
 * verbatim ("this occurrence and every later one"). A plain confirm always passes `false`, so a
 * callback that does not care may keep ignoring its argument.
 */
export type DeleteCallback = (options: { forFullSeries: boolean }) => Promise<void>;

export type DeleteDialogOptions = {
  /**
   * The item belongs to a series. The dialog then offers two destructive choices instead of one —
   * deleting an occurrence and deleting the rest of the rule are different acts, and the user is
   * the only one who knows which was meant.
   */
  isSeries?: boolean;
  /** One line under the title, for the context the title cannot carry. */
  description?: string;
  /**
   * `"warning"` sets the description in red: the delete takes recorded work with it. Absent, the
   * description stays the quiet grey line of a plain confirm.
   */
  tone?: "warning";
};

export type DeleteDialogController = {
  isOpen: boolean;
  title: string;
  description: string | null;
  tone: "warning" | null;
  isSeries: boolean;
  error: string | null;
  isLoading: boolean;
  /** Which button is in flight, so only the pressed one carries the spinner. */
  pendingScope: "single" | "series" | null;

  openDelete: (
    titleText: string,
    callback: DeleteCallback,
    options?: DeleteDialogOptions,
  ) => void;
  closeDelete: () => void;
  confirmDelete: (options?: { forFullSeries?: boolean }) => Promise<void>;
};

export const useDeleteDialog = (): DeleteDialogController => {
  const [isOpen, setIsOpen] = useState(false);
  const [onDelete, setOnDelete] = useState<DeleteCallback | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState<string | null>(null);
  const [tone, setTone] = useState<"warning" | null>(null);
  const [isSeries, setIsSeries] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [pendingScope, setPendingScope] = useState<"single" | "series" | null>(null);

  const isLoading = pendingScope != null;

  const openDelete = useCallback(
    (titleText: string, callback: DeleteCallback, options?: DeleteDialogOptions) => {
      setOnDelete(() => callback);
      setTitle(titleText);
      setDescription(options?.description ?? null);
      setTone(options?.tone ?? null);
      setIsSeries(options?.isSeries ?? false);
      setError(null);
      setPendingScope(null);
      setIsOpen(true);
    },
    [],
  );

  const closeDelete = useCallback(() => {
    if (isLoading) return;

    setIsOpen(false);
    setOnDelete(null);
    setError(null);
  }, [isLoading]);

  const confirmDelete = useCallback(
    async ({ forFullSeries = false }: { forFullSeries?: boolean } = {}) => {
      if (!onDelete) return;

      try {
        setPendingScope(forFullSeries ? "series" : "single");
        setError(null);

        await onDelete({ forFullSeries });

        setIsOpen(false);
        setOnDelete(null);
        setError(null);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setPendingScope(null);
      }
    },
    [onDelete],
  );

  return {
    isOpen,
    title,
    description,
    tone,
    isSeries,
    error,
    isLoading,
    pendingScope,
    openDelete,
    closeDelete,
    confirmDelete,
  };
};
